/**
 * Sprint H · #11 · AEATDeadlinesBanner
 *
 * Banner compacto en Dashboard con los próximos deadlines AEAT (≤30 días).
 * Visible solo para admin/gestor con plan tax_models_aeat. Cada modelo es
 * un chip clicable que abre /tax pre-seleccionado en ese modelo.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { CalendarBlank, ArrowRight, Warning } from "@phosphor-icons/react";

const MODEL_INFO = {
  "303": { name: "IVA", color: "text-[var(--ag-gold)]" },
  "111": { name: "IRPF Prof.", color: "text-[var(--ag-gold)]" },
  "115": { name: "IRPF Alquiler", color: "text-[var(--ag-gold)]" },
  "130": { name: "Pago Frac.", color: "text-[var(--ag-gold)]" },
  "390": { name: "Anual IVA", color: "text-purple-700" },
  "190": { name: "Anual 111", color: "text-purple-700" },
  "180": { name: "Anual 115", color: "text-purple-700" },
};

export function AEATDeadlinesBanner() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tax/deadlines?lookahead_days=60")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || items.length === 0) return null;

  // Agrupar por deadline date (los 4 trimestrales caen el mismo día)
  const byDate = {};
  for (const it of items) {
    (byDate[it.deadline_date] = byDate[it.deadline_date] || []).push(it);
  }
  const groups = Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b));
  const urgent = items.some((i) => i.days_left <= 7);

  return (
    <div className={`ag-card p-4 ${urgent ? "border-red-300 bg-red-50/40" : "border-amber-300 bg-amber-50/40"}`}
         data-testid="aeat-deadlines-banner">
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${urgent ? "bg-red-100" : "bg-amber-100"}`}>
          {urgent ? <Warning size={18} weight="fill" className="text-red-600" /> : <CalendarBlank size={18} weight="fill" className="text-amber-600" />}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${urgent ? "text-red-700" : "text-amber-700"}`}>
            Plazos AEAT próximos
          </div>
          <div className="font-display text-lg font-bold tracking-tight text-[#1A1A1A] mt-0.5" data-testid="aeat-banner-title">
            {urgent ? "¡Atención! Plazo en menos de 7 días" : `${items.length} modelo${items.length === 1 ? "" : "s"} a presentar`}
          </div>
        </div>
        <Link to="/tax" className="text-xs text-[var(--ag-gold)] hover:underline font-medium flex items-center gap-1 shrink-0" data-testid="aeat-banner-cta">
          Ir a Impuestos <ArrowRight size={12} weight="bold" />
        </Link>
      </div>

      <div className="mt-3 space-y-2">
        {groups.map(([deadlineDate, group]) => {
          const dl = new Date(deadlineDate + "T00:00:00");
          const dlLabel = dl.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
          const days = group[0].days_left;
          const urgentGroup = days <= 7;
          return (
            <div key={deadlineDate}
                 className={`flex items-center flex-wrap gap-2 p-2 rounded-sm border ${urgentGroup ? "border-red-200 bg-white" : "border-amber-200 bg-white"}`}
                 data-testid={`aeat-deadline-group-${deadlineDate}`}>
              <div className="text-xs">
                <span className={`font-bold ${urgentGroup ? "text-red-700" : "text-amber-700"}`}>
                  {days === 0 ? "HOY" : days === 1 ? "Mañana" : `En ${days} días`}
                </span>
                <span className="text-gray-500 ml-1.5">· {dlLabel}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 ml-auto">
                {group.map((it) => (
                  <Link key={`${it.model}-${it.period_label}`} to={`/tax?model=${it.model}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 rounded text-[11px] font-mono-tab transition-colors"
                        data-testid={`aeat-model-chip-${it.model}`}>
                    <b className={MODEL_INFO[it.model]?.color || "text-gray-700"}>{it.model}</b>
                    <span className="text-gray-500">·</span>
                    <span className="text-gray-600">{it.period_label}</span>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
